import { STOPWATCH_IDLE, stopwatchLap, stopwatchToggle, timerAdd, timerIdle, timerPause, timerStart, type StopwatchState, type TimerState } from '@obv/core';
import { create } from 'zustand';

export type ClockTab = 'alarms' | 'stopwatch' | 'timer';

export type Ringing = { kind: 'alarm'; alarmId: string; label: string; time: string; oneTime: boolean } | { kind: 'timer' };

export interface Snooze {
  alarmId: string;
  label: string;
  time: string;
  oneTime: boolean;
  at: number;
}

export const SNOOZE_MS = 9 * 60_000;
const DEFAULT_TIMER_MS = 5 * 60_000;
const STORAGE_KEY = 'obv-clock';

interface Persisted {
  stopwatch: StopwatchState;
  timer: TimerState;
  snooze: Snooze | null;
  /** "alarmId@YYYY-MM-DD HH:MM" of the last ring, so a reload in the same minute doesn't ring twice. */
  lastFired: string | null;
}

interface ClockState extends Persisted {
  tab: ClockTab;
  ringing: Ringing | null;
  setTab: (t: ClockTab) => void;
  toggleStopwatch: () => void;
  lap: () => void;
  resetStopwatch: () => void;
  setTimerDuration: (ms: number) => void;
  startTimer: () => void;
  pauseTimer: () => void;
  addTimer: (ms: number) => void;
  resetTimer: () => void;
  ring: (r: Ringing, firedKey?: string) => void;
  snoozeRinging: () => void;
  /** "+1 min" on a finished timer: runs one more minute, keeping the original duration. */
  extendFinishedTimer: (ms: number) => void;
  stopRinging: () => void;
}

// A timer, stopwatch or snooze is this display's own short-lived state, so it lives in this
// browser only; storage can be unavailable (private mode), so every access is guarded.
function load(): Partial<Persisted> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Persisted>) : {};
  } catch {
    return {};
  }
}

function save(s: Persisted) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Not persisted; the in-memory state still works.
  }
}

const initial = load();

export const useClockTools = create<ClockState>((set, get) => ({
  tab: 'alarms',
  ringing: null,
  stopwatch: initial.stopwatch || STOPWATCH_IDLE,
  timer: initial.timer || timerIdle(DEFAULT_TIMER_MS),
  snooze: initial.snooze || null,
  lastFired: initial.lastFired || null,
  setTab: (tab) => set({ tab }),
  toggleStopwatch: () => set((s) => ({ stopwatch: stopwatchToggle(s.stopwatch, Date.now()) })),
  lap: () => set((s) => ({ stopwatch: stopwatchLap(s.stopwatch, Date.now()) })),
  resetStopwatch: () => set({ stopwatch: STOPWATCH_IDLE }),
  setTimerDuration: (ms) => set({ timer: timerIdle(Math.max(1000, ms)) }),
  startTimer: () => set((s) => ({ timer: timerStart(s.timer, Date.now()) })),
  pauseTimer: () => set((s) => ({ timer: timerPause(s.timer, Date.now()) })),
  addTimer: (ms) => set((s) => ({ timer: timerAdd(s.timer, ms) })),
  resetTimer: () => set((s) => ({ timer: timerIdle(s.timer.durationMs) })),
  ring: (ringing, firedKey) => set(firedKey ? { ringing, lastFired: firedKey } : { ringing }),
  snoozeRinging: () => {
    const r = get().ringing;
    if (!r || r.kind !== 'alarm') return;
    set({ ringing: null, snooze: { alarmId: r.alarmId, label: r.label, time: r.time, oneTime: r.oneTime, at: Date.now() + SNOOZE_MS } });
  },
  extendFinishedTimer: (ms) => set((s) => ({ ringing: null, timer: { durationMs: s.timer.durationMs, endsAt: Date.now() + ms, pausedLeft: null } })),
  stopRinging: () => {
    const r = get().ringing;
    // A finished timer goes back to its last duration, ready to run again.
    set(r && r.kind === 'timer' ? { ringing: null, timer: timerIdle(get().timer.durationMs) } : { ringing: null });
  },
}));

useClockTools.subscribe((s) => save({ stopwatch: s.stopwatch, timer: s.timer, snooze: s.snooze, lastFired: s.lastFired }));
